/**
 * Maps API eventEmployees into SelectStaffMemberModal / Edit Event shape.
 * @param {Array<Record<string, unknown>>} [eventEmployees]
 * @returns {Array<Record<string, unknown>>}
 */
export function mapEventEmployeesForStaffModal(eventEmployees = []) {
  return (eventEmployees || [])
    .filter((item) => !item?.isDeletedForThisSchedulerEvent)
    .map((item) => ({
      ...item,
      id: item?.id,
      schedulerEventId: item?.schedulerEventId,
      userId: item?.userId ?? item?.user?.id,
      officeId: item?.officeId ?? item?.office?.id,
      IsDeleted: false,
      isDeleted: false,
      isFromDetail: true,
    }));
}

/**
 * Builds UpdateFlags for staff assign/unassign, mirroring Edit Event verifyIfUpdated.
 * @param {Record<string, unknown>} eventDetail
 * @param {Array<Record<string, unknown>>} memberIds - modal result (may include soft-deleted)
 * @returns {{
 *   UpdateFlags: Record<string, unknown>,
 *   changedCheck: boolean,
 *   eventEmployeesPayload: Array<Record<string, unknown>>,
 * }}
 */
export function buildEmployeeAssignUpdateFlags(eventDetail, memberIds = []) {
  const employeeOfficeKey = (e) => `${e?.userId ?? ""}:${e?.officeId ?? ""}`;

  const removedEmployeeRowIdsFromMarked = memberIds.reduce((acc, curr) => {
    const isDeleted = curr?.isDeleted || curr?.IsDeleted;
    const rowId = curr?.id;
    if (isDeleted && rowId != null && rowId !== 0) {
      acc.push(rowId);
    }
    return acc;
  }, []);

  const originalActiveKeys = new Set(
    (eventDetail?.eventEmployees || [])
      .filter((e) => !e?.isDeletedForThisSchedulerEvent)
      .map(employeeOfficeKey),
  );

  const activeCurrentEmployees = memberIds.filter(
    (curr) => !(curr?.isDeleted || curr?.IsDeleted),
  );
  const currentActiveKeys = new Set(
    activeCurrentEmployees.map(employeeOfficeKey),
  );

  const removedEmployeeRowIdsFromReplacedRows = (
    eventDetail?.eventEmployees || []
  )
    .filter((e) => !e?.isDeletedForThisSchedulerEvent)
    .filter((e) => {
      const key = employeeOfficeKey(e);
      return !currentActiveKeys.has(key) && e?.id != null && e.id !== 0;
    })
    .map((e) => e.id);

  const removedEmployeeRowIds = [
    ...new Set([
      ...removedEmployeeRowIdsFromMarked,
      ...removedEmployeeRowIdsFromReplacedRows,
    ]),
  ];

  const employeesRosterChanged =
    originalActiveKeys.size !== currentActiveKeys.size ||
    [...currentActiveKeys].some((k) => !originalActiveKeys.has(k)) ||
    [...originalActiveKeys].some((k) => !currentActiveKeys.has(k));
  const employeesRosterOrRemovalChanged =
    removedEmployeeRowIds.length > 0 || employeesRosterChanged;

  const touchedEmployeeUserIds = new Set();
  activeCurrentEmployees.forEach((e) => {
    const key = employeeOfficeKey(e);
    if (!originalActiveKeys.has(key) && e?.userId != null) {
      touchedEmployeeUserIds.add(e.userId);
    }
  });
  (eventDetail?.eventEmployees || [])
    .filter((e) => !e?.isDeletedForThisSchedulerEvent)
    .forEach((e) => {
      const key = employeeOfficeKey(e);
      if (!currentActiveKeys.has(key) && e?.userId != null) {
        touchedEmployeeUserIds.add(e.userId);
      }
    });

  let isShiftOfficeChange = false;
  if (eventDetail?.isShift) {
    const origOfficeByUserId = new Map();
    (eventDetail?.eventEmployees || [])
      .filter((e) => !e?.isDeletedForThisSchedulerEvent)
      .forEach((e) => {
        if (e?.userId != null) {
          origOfficeByUserId.set(e.userId, e.officeId);
        }
      });
    for (const e of activeCurrentEmployees) {
      if (
        e?.userId != null &&
        origOfficeByUserId.has(e.userId) &&
        origOfficeByUserId.get(e.userId) !== e.officeId
      ) {
        isShiftOfficeChange = true;
        break;
      }
    }
  }

  const UpdateFlags = {
    isTitleEdit: false,
    isStartTimeEdit: false,
    isEndTimeEdit: false,
    isAllDayEventEdit: false,
    isNoteEdit: false,
    isLocationEdit: false,
    isReminderBeforeInMinsEdit: false,
    isEnterpriseEdit: false,
    isOfficeIdEdit: false,
    EmployeeIds: employeesRosterOrRemovalChanged
      ? [...touchedEmployeeUserIds]
      : [],
    RemovedEmployeeRowIds: removedEmployeeRowIds,
    IsShiftOfficeChange: Boolean(eventDetail?.isShift && isShiftOfficeChange),
    RoleIds: [],
    IsRepeatTypeChange: false,
    IsEventDateChange: false,
    IsRepeatedEndDateChange: false,
    IsBlockForPatientChange: false,
    tagsIds: false,
  };

  return {
    UpdateFlags,
    changedCheck: Object.values(UpdateFlags).some((value) =>
      typeof value === "boolean" ? value : value?.length > 0,
    ),
    eventEmployeesPayload: activeCurrentEmployees.map((employee) => ({
      ...employee,
      id: employee?.id ?? 0,
      schedulerEventId: employee?.schedulerEventId ?? eventDetail?.id,
      userId: employee?.userId ?? employee?.user?.id,
      officeId: employee?.officeId ?? employee?.office?.id,
      IsDeleted: false,
      isDeleted: false,
    })),
  };
}
